import React, { useState, useEffect } from 'react';
import { Card, Tabs, Table, Select, DatePicker, Input, Button, Space, Tag, Statistic, Row, Col, Spin, Alert, Drawer, Typography } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import IPLocation from '../../components/IPLocation';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Option } = Select;
const { Title } = Typography;

const LoginLogs = () => {
  const { user, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clientLogs, setClientLogs] = useState([]);
  const [clientLogsSummary, setClientLogsSummary] = useState([]);
  const [teacherLogs, setTeacherLogs] = useState([]);
  const [teacherLogsSummary, setTeacherLogsSummary] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    schoolId: null,
    classId: null,
    teacherId: null,
    clientType: null,
    loginType: null,
    status: null,
    dateRange: null
  });
  
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailType, setDetailType] = useState('teacher');
  const [itemDetails, setItemDetails] = useState([]);
  const [detailPagination, setDetailPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchClientLogs();
    fetchTeacherLogsSummary();
    fetchClientLogsSummary();
    fetchStats();
  }, []);

  const fetchClientLogs = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: params.page || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        ...filters,
        ...params
      };

      queryParams.page = parseInt(queryParams.page);
      queryParams.pageSize = parseInt(queryParams.pageSize);

      if (filters.dateRange && filters.dateRange.length === 2) {
        queryParams.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        queryParams.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.get('/login-logs/clients', { params: queryParams });
      if (response.data.success) {
        setClientLogs(response.data.data.logs);
        setPagination({
          ...pagination,
          total: response.data.data.pagination.total,
          current: response.data.data.pagination.page
        });
      }
    } catch (error) {
      console.error('获取客户端登录记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherLogsSummary = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: params.page || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        ...filters,
        ...params
      };

      queryParams.page = parseInt(queryParams.page);
      queryParams.pageSize = parseInt(queryParams.pageSize);

      if (filters.dateRange && filters.dateRange.length === 2) {
        queryParams.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        queryParams.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.get('/login-logs/teachers/summary', { params: queryParams });
      if (response.data.success) {
        setTeacherLogsSummary(response.data.data.logs);
        setPagination({
          ...pagination,
          total: response.data.data.pagination.total,
          current: response.data.data.pagination.page
        });
      }
    } catch (error) {
      console.error('获取教师登录汇总失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientLogsSummary = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: params.page || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        ...filters,
        ...params
      };

      queryParams.page = parseInt(queryParams.page);
      queryParams.pageSize = parseInt(queryParams.pageSize);

      if (filters.dateRange && filters.dateRange.length === 2) {
        queryParams.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        queryParams.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.get('/login-logs/clients/summary', { params: queryParams });
      if (response.data.success) {
        setClientLogsSummary(response.data.data.logs);
        setPagination({
          ...pagination,
          total: response.data.data.pagination.total,
          current: response.data.data.pagination.page
        });
      }
    } catch (error) {
      console.error('获取客户端登录汇总失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherDetails = async (teacherId, params = {}) => {
    setDetailLoading(true);
    try {
      const queryParams = {
        page: params.page || detailPagination.current,
        pageSize: params.pageSize || detailPagination.pageSize,
        ...params
      };

      const response = await api.get(`/login-logs/teachers/${teacherId}/details`, { params: queryParams });
      if (response.data.success) {
        setItemDetails(response.data.data.logs);
        setDetailPagination({
          ...detailPagination,
          total: response.data.data.pagination.total,
          current: response.data.data.pagination.page
        });
      }
    } catch (error) {
      console.error('获取教师登录详情失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchClientDetails = async (classId, params = {}) => {
    setDetailLoading(true);
    try {
      const queryParams = {
        page: params.page || detailPagination.current,
        pageSize: params.pageSize || detailPagination.pageSize,
        ...params
      };

      const response = await api.get(`/login-logs/clients/${classId}/details`, { params: queryParams });
      if (response.data.success) {
        setItemDetails(response.data.data.logs);
        setDetailPagination({
          ...detailPagination,
          total: response.data.data.pagination.total,
          current: response.data.data.pagination.page
        });
      }
    } catch (error) {
      console.error('获取客户端登录详情失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const queryParams = {};
      if (filters.dateRange && filters.dateRange.length === 2) {
        queryParams.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        queryParams.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.get('/login-logs/stats', { params: queryParams });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
    }
  };

  const handleSearch = () => {
    fetchClientLogs({ page: 1 });
    fetchClientLogsSummary({ page: 1 });
    fetchTeacherLogsSummary({ page: 1 });
    fetchStats();
  };

  const handleReset = () => {
    setFilters({
      schoolId: null,
      classId: null,
      teacherId: null,
      clientType: null,
      loginType: null,
      status: null,
      dateRange: null
    });
  };

  const handleViewTeacherDetails = (teacher) => {
    setSelectedItem(teacher);
    setDetailType('teacher');
    setDetailDrawerVisible(true);
    fetchTeacherDetails(teacher.teacher_id);
  };

  const handleViewClientDetails = (client) => {
    setSelectedItem(client);
    setDetailType('client');
    setDetailDrawerVisible(true);
    fetchClientDetails(client.class_id);
  };

  // 判断真实在线状态的函数
  const getRealStatus = (record) => {
    const now = new Date();
    const loginTime = new Date(record.login_time);
    const logoutTime = record.logout_time ? new Date(record.logout_time) : null;
    
    // 如果有退出时间，则为离线
    if (logoutTime) {
      return 'offline';
    }
    
    // 如果登录时间超过1小时，视为离线
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    if (loginTime < oneHourAgo) {
      return 'offline';
    }
    
    // 其他情况按原状态显示
    return record.status;
  };

  // 渲染状态标签的函数
  const renderStatus = (record) => {
    const realStatus = getRealStatus(record);
    const statusMap = {
      online: { text: '在线', color: 'green' },
      offline: { text: '离线', color: 'red' },
      error: { text: '错误', color: 'volcano' }
    };
    const config = statusMap[realStatus] || { text: realStatus, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const clientColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '班级名称',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: '学校名称',
      dataIndex: 'school_name',
      key: 'school_name',
    },
    {
      title: '客户端类型',
      dataIndex: 'client_type',
      key: 'client_type',
      render: (type) => {
        const typeMap = {
          desktop: { text: '桌面端', color: 'blue' },
          web: { text: 'Web端', color: 'green' },
          mobile: { text: '移动端', color: 'orange' }
        };
        const config = typeMap[type] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => renderStatus(record)
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => {
        if (!ip || ip === '未知') return '-';
        
        const cleanIp = ip.replace(/^::ffff:/, '');
        
        return (
          <div>
            <div>{cleanIp}</div>
            <IPLocation ip={cleanIp} />
          </div>
        );
      }
    },
    {
      title: '登录时间',
      dataIndex: 'login_time',
      key: 'login_time',
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    },
    {
      title: '会话时长',
      dataIndex: 'session_duration',
      key: 'session_duration',
      render: (duration) => {
        if (!duration) return '-';
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        return `${hours}h ${minutes}m`;
      }
    }
  ];

  // 客户端登录记录汇总表格列
  const clientSummaryColumns = [
    {
      title: '班级名称',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: '学校名称',
      dataIndex: 'school_name',
      key: 'school_name',
      render: (text) => text || '未设置',
    },
    {
      title: '客户端类型',
      dataIndex: 'client_type',
      key: 'client_type',
      render: (type) => {
        const typeMap = {
          desktop: { text: '桌面端', color: 'blue' },
          web: { text: 'Web端', color: 'green' },
          mobile: { text: '移动端', color: 'orange' }
        };
        const config = typeMap[type] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '当前状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          online: { text: '在线', color: 'green' },
          offline: { text: '离线', color: 'red' },
          error: { text: '错误', color: 'volcano' }
        };
        const config = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '登录次数',
      dataIndex: 'login_count',
      key: 'login_count',
      render: (count) => <Tag color="blue">{count}次</Tag>,
    },
    {
      title: '最近登录IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => {
        if (!ip || ip === '未知') return '-';
        
        const cleanIp = ip.replace(/^::ffff:/, '');
        
        return (
          <div>
            <div>{cleanIp}</div>
            <IPLocation ip={cleanIp} />
          </div>
        );
      }
    },
    {
      title: '最近登录时间',
      dataIndex: 'last_login_time',
      key: 'last_login_time',
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewClientDetails(record)}
        >
          查看详情
        </Button>
      ),
    }
  ];

  const teacherSummaryColumns = [
    {
      title: '教师姓名',
      dataIndex: 'teacher_name',
      key: 'teacher_name',
    },
    {
      title: '学校名称',
      dataIndex: 'school_name',
      key: 'school_name',
      render: (text) => text || '未设置',
    },
    {
      title: '登录方式',
      dataIndex: 'login_type',
      key: 'login_type',
      render: (type) => {
        const typeMap = {
          web: { text: 'Web登录', color: 'blue' },
          miniprogram: { text: '微信小程序', color: 'green' },
          mobile: { text: '移动应用', color: 'orange' },
          desktop: { text: '桌面应用', color: 'purple' }
        };
        const config = typeMap[type] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '当前状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          online: { text: '在线', color: 'green' },
          offline: { text: '离线', color: 'red' },
          error: { text: '错误', color: 'volcano' }
        };
        const config = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '登录次数',
      dataIndex: 'login_count',
      key: 'login_count',
      render: (count) => <Tag color="blue">{count}次</Tag>,
    },
    {
      title: '最近登录IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => {
        if (!ip || ip === '未知') return '-';
        
        const cleanIp = ip.replace(/^::ffff:/, '');
        
        return (
          <div>
            <div>{cleanIp}</div>
            <IPLocation ip={cleanIp} />
          </div>
        );
      }
    },
    {
      title: '最近登录时间',
      dataIndex: 'last_login_time',
      key: 'last_login_time',
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewTeacherDetails(record)}
        >
          查看详情
        </Button>
      ),
    }
  ];

  // 教师详细登录记录表格列
  const teacherDetailColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '登录方式',
      dataIndex: 'login_type',
      key: 'login_type',
      render: (type) => {
        const typeMap = {
          web: { text: 'Web登录', color: 'blue' },
          miniprogram: { text: '微信小程序', color: 'green' },
          mobile: { text: '移动应用', color: 'orange' },
          desktop: { text: '桌面应用', color: 'purple' }
        };
        const config = typeMap[type] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => renderStatus(record)
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => {
        if (!ip || ip === '未知') return '-';
        
        const cleanIp = ip.replace(/^::ffff:/, '');
        
        return (
          <div>
            <div>{cleanIp}</div>
            <IPLocation ip={cleanIp} />
          </div>
        );
      }
    },
    {
      title: '登录时间',
      dataIndex: 'login_time',
      key: 'login_time',
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    },
    {
      title: '退出时间',
      dataIndex: 'logout_time',
      key: 'logout_time',
      render: (text) => text ? new Date(text).toLocaleString() : '未退出'
    },
    {
      title: '会话时长',
      key: 'session_duration',
      render: (_, record) => {
        if (!record.login_time) return '-';
        
        const loginTime = new Date(record.login_time);
        const logoutTime = record.logout_time ? new Date(record.logout_time) : new Date();
        const duration = Math.floor((logoutTime - loginTime) / 1000);
        
        if (duration <= 0) return '-';
        
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        return `${hours}h ${minutes}m`;
      }
    }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="客户端总登录次数" 
              value={stats.clientStats?.total_client_logins || 0} 
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="当前在线客户端" 
              value={stats.clientStats?.online_clients || 0} 
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="教师总登录次数" 
              value={stats.teacherStats?.total_teacher_logins || 0} 
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="当前在线教师" 
              value={stats.teacherStats?.online_teachers || 0} 
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            placeholder={['开始日期', '结束日期']}
          />
          <Select
            placeholder="状态"
            style={{ width: 120 }}
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            allowClear
          >
            <Option value="online">在线</Option>
            <Option value="offline">离线</Option>
            <Option value="error">错误</Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            重置
          </Button>
          <Button icon={<DownloadOutlined />}>
            导出
          </Button>
        </Space>
      </Card>

      <Card>
        <Tabs defaultActiveKey="clients" onChange={(activeKey) => {
          if (activeKey === 'teachers') {
            fetchTeacherLogsSummary();
          } else if (activeKey === 'clients') {
            fetchClientLogsSummary();
          }
        }}>
          <TabPane tab="客户端登录记录" key="clients">
            <Table
              columns={clientSummaryColumns}
              dataSource={clientLogsSummary}
              rowKey="class_id"
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  setPagination({ ...pagination, current: page, pageSize });
                  fetchClientLogsSummary({ page, pageSize });
                }
              }}
            />
          </TabPane>
          <TabPane tab="教师登录记录" key="teachers">
            <Table
              columns={teacherSummaryColumns}
              dataSource={teacherLogsSummary}
              rowKey="teacher_id"
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  setPagination({ ...pagination, current: page, pageSize });
                  fetchTeacherLogsSummary({ page, pageSize });
                }
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      <Drawer
        title={`${detailType === 'teacher' ? '教师' : '客户端'}登录详情 - ${selectedItem?.teacher_name || selectedItem?.class_name || '未命名'}`}
        width={800}
        visible={detailDrawerVisible}
        onClose={() => {
          setDetailDrawerVisible(false);
          setSelectedItem(null);
        }}
        footer={null}
      >
        <Table
          columns={detailType === 'teacher' ? teacherDetailColumns : clientColumns}
          dataSource={itemDetails}
          rowKey="id"
          loading={detailLoading}
          pagination={{
            ...detailPagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setDetailPagination({ ...detailPagination, current: page, pageSize });
              if (detailType === 'teacher') {
                fetchTeacherDetails(selectedItem.teacher_id, { page, pageSize });
              } else {
                fetchClientDetails(selectedItem.class_id, { page, pageSize });
              }
            }
          }}
        />
      </Drawer>
    </div>
  );
};

export default LoginLogs; 